// Using static/mock data instead of API calls
import type {
  AssessmentTemplateResponse,
  Assessment,
  Question,
  QuestionResponse,
  ReorderRequest
} from '../types/assessments'
import {
  mockTemplates,
  createMockAssessment,
  getMockAssessment,
  getAllMockAssessments,
  updateMockAssessment,
  deleteMockAssessment,
  getMockQuestions,
  addMockQuestion,
  updateMockQuestion,
  deleteMockQuestion,
  reorderMockQuestions,
  getTemplateQuestions
} from '../data/mockAssessments'
import axios from 'axios'

// Simulate API delay
const delay = (ms: number = 500) => new Promise((resolve) => setTimeout(resolve, ms))

// Assessment Templates
export const assessmentTemplatesApi = {
  getAll: async (): Promise<AssessmentTemplateResponse> => {
    await delay(300)
    return {
      success: true,
      count: mockTemplates.length,
      data: mockTemplates
    }
  },

  getTemplateQuestions: async (templateId: string): Promise<QuestionResponse[]> => {
    await delay(300)
    return getTemplateQuestions(templateId)
  }
}

// Assessments
export const assessmentsApi = {
  getAll: async (): Promise<Assessment[]> => {
    await delay(300)
    return getAllMockAssessments()
  },

  getById: async (id: string): Promise<Assessment> => {
    await delay(300)
    const assessment = getMockAssessment(id)
    if (!assessment) {
      throw new Error('Assessment not found')
    }
    return assessment
  },

  create: async (templateId: string, data: Partial<Assessment>): Promise<Assessment> => {
    try {
      const token = localStorage.getItem('auth-token')
      const apiUrl = process.env.REACT_APP_RESUME_PARSER_URL || 'http://localhost:4000'

      const payload = {
        title: data.title || '',
        subtitle: data.subtitle || '',
        description: data.description || '',
        icon: data.icon || '',
        color: data.color || '#3498db',
        category: data.category || '',
        totalMarks: null,
        passingMarks: null,
        durationMinutes: null,
        status: 'ACTIVE',
        questions: []
      }

      const response = await axios.post(`${apiUrl}/api/assessment/`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      return response.data
    } catch (error: any) {
      console.error('Failed to create assessment:', error)
      // Fallback to mock data if API fails
      await delay(500)
      return createMockAssessment(templateId, data)
    }
  },

  update: async (id: string, data: Partial<Assessment>): Promise<Assessment> => {
    await delay(300)
    const updated = updateMockAssessment(id, data)
    if (!updated) {
      throw new Error('Assessment not found')
    }
    return updated
  },

  delete: async (id: string): Promise<void> => {
    await delay(300)
    const deleted = deleteMockAssessment(id)
    if (!deleted) {
      throw new Error('Assessment not found')
    }
  }
}

// Questions
export const questionsApi = {
  getByAssessmentId: async (assessmentId: string): Promise<QuestionResponse[]> => {
    await delay(300)
    return getMockQuestions(assessmentId)
  },

  create: async (
    assessmentId: string,
    question: Omit<Question, 'id' | 'uuid'>
  ): Promise<QuestionResponse> => {
    await delay(400)
    return addMockQuestion(assessmentId, question)
  },

  update: async (
    assessmentId: string,
    questionId: number,
    question: Partial<Question>
  ): Promise<QuestionResponse> => {
    await delay(300)
    const updated = updateMockQuestion(assessmentId, questionId, question)
    if (!updated) {
      throw new Error('Question not found')
    }
    return updated
  },

  delete: async (assessmentId: string, questionId: number): Promise<void> => {
    await delay(300)
    const deleted = deleteMockQuestion(assessmentId, questionId)
    if (!deleted) {
      throw new Error('Question not found')
    }
  },

  reorder: async (assessmentId: string, orders: ReorderRequest): Promise<void> => {
    await delay(300)
    reorderMockQuestions(assessmentId, orders.orders)
  }
}
